// libs/election-api-types/src/election-api-types.ts
import {
  array,
  boolean,
  discriminatedUnion,
  enum as enum2,
  literal,
  number,
  object,
  record,
  string
} from "zod";
var partyMetaSchema = object({
  partyName: string(),
  shortPartyName: string().optional()
});
var partyInfoSchema = record(string(), partyMetaSchema);
var seatMetaSchema = object({
  seatGuide: string().optional()
});
var seatInfoSchema = record(string(), seatMetaSchema);
var areaCodeSchema = enum2([
  "ACT",
  "NSW",
  "NT",
  "QLD",
  "SA",
  "TAS",
  "VIC",
  "WA",
  "Federal"
]);
var predictionBasisSchema = enum2(["Algorithm", "Manual", "None"]);
var predictionCertaintySchema = enum2([
  "InDoubt",
  "TooClose",
  "Probable",
  "Definite"
]);
var partyDtoSchema = object({
  state: string(),
  partyCode: string(),
  partyName: string().optional(),
  shortPartyName: string().optional(),
  isCoalition: boolean(),
  swingPct: number().nullable(),
  seatsWon: number(),
  seatsAhead: number(),
  seatsChanged: number(),
  seatsPredicted: number(),
  seatsLost: number().optional(),
  seatsGained: number().optional(),
  votePct: number(),
  voteCount: number(),
  tpp: number(),
  tppChange: number(),
  tppHistorical: number(),
  tppSwingPct: number().nullable()
});
var candidatesDtoSchema = object({
  candidateId: number(),
  firstName: string().nullable(),
  lastName: string(),
  partyCode: string(),
  partyName: string().optional(),
  shortPartyName: string().optional(),
  photoUrl: string(),
  seatId: string(),
  isIncumbent: boolean(),
  swingPct: number().nullable(),
  primaryVotesPct: number(),
  primaryVotes: number()
});
var twoPartyPreferredDtoSchema = object({
  seatCode: string(),
  partyCode: string(),
  candidateId: number(),
  candidateFirstName: string().nullable(),
  candidateLastName: string(),
  candidatePartyCode: string().nullable(),
  candidatePartyName: string().optional(),
  candidateShortPartyName: string().optional(),
  incumbent: boolean(),
  primaryVotes: number(),
  changePrimaryPct: number().nullable(),
  changeTcpPct: number().nullable(),
  tcpVotesPctCurrent: number().nullable(),
  winner: boolean()
});
var seatDtoSchema = object({
  seatId: string(),
  seatName: string(),
  seatRegion: string(),
  state: string(),
  shortState: string().optional(),
  postcodes: array(number()).readonly(),
  incumbentName: string().nullable(),
  incumbentParty: string().nullable(),
  incumbentIsCoalition: boolean(),
  incumbentPartyCode: string().optional(),
  predictionBasis: predictionBasisSchema.optional(),
  predictionCertainty: predictionCertaintySchema.optional(),
  predictionParty: string().optional(),
  candidates: array(candidatesDtoSchema),
  twoPartyPreferred: array(twoPartyPreferredDtoSchema),
  swingPct: number().nullable(),
  lastUpdated: string().nullable(),
  isKeySeat: boolean().optional(),
  keySeatIndex: number().optional(),
  pctCounted: number().nullable().optional(),
  seatGuide: string().optional()
});
var areaDtoSchema = object({
  electionId: number(),
  areaCode: string(),
  areaName: string(),
  parties: array(partyDtoSchema),
  pctCounted: number()
});
var electionConfigSchema = object({
  enabled: boolean(),
  electionApi: discriminatedUnion("source", [
    object({
      source: literal("interknowlogy"),
      electionId: string(),
      electionCountryCode: string(),
      frequency: number(),
      totalAreaId: string().optional()
    })
  ]),
  mapApiUrl: string().optional().nullable(),
  seatsToWin: number(),
  seatsTotal: number(),
  barTotalSeats: number(),
  electionTitle: string(),
  electionDate: string().datetime(),
  electionType: string(),
  state: string().optional(),
  previousElectionId: string().optional(),
  keySeatIds: array(string()),
  partyInfo: partyInfoSchema.optional(),
  seatInfo: seatInfoSchema.optional(),
  racePartyIds: array(string()).optional(),
  headToHeadPartyIds: array(string()).length(2).optional()
});
var isElectionConfig = (obj) => {
  return electionConfigSchema.safeParse(obj).success;
};
var predictedSeatSchema = object({
  partyCode: string(),
  count: number()
});
var seatsPredictedSchema = record(string(), array(predictedSeatSchema));

// libs/election-api-types/src/election-response-types.ts
import {
  array as array2,
  nullable,
  number as number2,
  object as object2,
  string as string2
} from "zod";
var transformedDataSchema = object2({
  areas: array2(areaDtoSchema),
  seats: array2(seatDtoSchema),
  needle: number2().optional(),
  totalArea: nullable(areaDtoSchema).optional(),
  lastModified: string2().datetime()
});
var electionDataResponseSchema = object2({
  data: transformedDataSchema.optional(),
  config: electionConfigSchema.optional()
});
var electionsElectionDefinition = object2({
  wanElectionId: string2(),
  objectKey: string2()
});
var electionsResponseSchema = array2(electionsElectionDefinition);
export {
  areaCodeSchema,
  areaDtoSchema,
  candidatesDtoSchema,
  electionConfigSchema,
  electionDataResponseSchema,
  electionsElectionDefinition,
  electionsResponseSchema,
  isElectionConfig,
  partyDtoSchema,
  predictedSeatSchema,
  seatDtoSchema,
  transformedDataSchema,
  twoPartyPreferredDtoSchema
};
//# sourceMappingURL=index.js.map
